import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, isSameDay } from "date-fns";
import moment from "moment-timezone";

// --- COMPONENTS ---
import DateRangeFilter from "../components/DateRangeFilter";
import Loader from "../components/Loader";
import DownloadProgressModal from "../components/DownloadProgressModal";
import Modal from "../components/Modal";
import {
  getNameTint,
  getPriorityColors,
  getQueryTypeColors,
  getStatusColor,
  readableOn,
  REPORT_CELL_COLORS,
  getStatusColors,
  getTeamColors,
  getUpdateColors,
  getYesNoColors,
} from "../utils/badgeColors";
import FilterPanel from "../components/FilterPanel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ValueFilterDropdown from "../components/ValueFilterDropdown";
import { DATE_PRESETS } from "../utils/datePresets";
import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import ReportViewer from "../components/ReportViewer";
// import { BarChart3 } from "lucide-react";

// --- UTILS & ACTIONS ---
import {
  excelClientWiseReportCountApis,
  reportListsApis,
} from "../redux/report/reportActions";
import { exportToPDF } from "../utils/pdfExport";
import { downloadSupportTeamReport } from "../utils/supportTeamReport";
import { teamWiseApis } from "../redux/team/teamActions";
import { matchesAdvancedFilters } from "../utils/filterUtils";
import { calculateDuration, displayStatus, formatDateTimeDisplay, isPendingStatus, lastSevenDays, secondsToHoursMinutes, toAppTime, toDateValue } from "../utils/common";

import { historyListAPIs } from "../redux/tasks/taskActions";
import {
  selectHistoryApiList,
  selectHistoryApiApiListStatus,
} from "../redux/tasks/taskSelectors";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/reportTable.css";
import "../assets/css/reportSetup.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// --- HELPER: Convert Minutes to HH:MM ---
const formatToHHMM = (totalMinutes) => {
  if (!totalMinutes || isNaN(totalMinutes)) return "00:00";
  const h = Math.floor(totalMinutes / 60);
  const m = Math.floor(totalMinutes % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// --- HELPER: Timeline Colors ---
const getTimelineColor = (status) => {
  switch (status) {
    case "Complete":
      return "#2e7d32";
    case "Testing":
      return "#00838f";
    case "In Progress":
      return "#0d6efd";
    case "On Hold":
      return "#f57c00";
    case "Reassign":
    case "Not Possible":
      return "#d32f2f";
    default:
      return "#6c757d";
  }
};

// --- CUSTOM MULTI-SELECT STATUS DROPDOWN ---
const StatusFilterDropdown = ({ selectedStatuses, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const OPTIONS = ["Not Started", "In Progress", "On Hold", "Complete"];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSelection = (option) => {
    let newSelection = [...selectedStatuses];
    if (option === "All") {
      newSelection = ["All"];
    } else {
      if (newSelection.includes("All")) newSelection = [];
      if (newSelection.includes(option)) {
        newSelection = newSelection.filter((item) => item !== option);
      } else {
        newSelection.push(option);
      }
      if (newSelection.length === 0) newSelection = ["All"];
    }
    onChange(newSelection);
  };

  const getLabel = () => {
    if (selectedStatuses.includes("All")) return "All Status";
    if (selectedStatuses.length === 1) return selectedStatuses[0];
    return `${selectedStatuses.length} Selected`;
  };

  return (
    <div ref={wrapperRef} className={`position-relative ${className || ""}`}>
      <button
        className="btn btn-white border d-flex align-items-center justify-content-between shadow-sm w-100"
        style={{ height: "40px", fontSize: "0.85rem", fontWeight: 600, color: "#495057", borderRadius: "8px", backgroundColor: "#fff", minWidth: "160px" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center gap-2 text-truncate">
          {/* <i className="bi bi-funnel-fill text-primary opacity-75"></i> */}
          <span>{getLabel()}</span>
        </div>
        <i className="bi bi-chevron-down ms-2 text-muted" style={{ fontSize: "0.7rem" }}></i>
      </button>
      {isOpen && (
        <div className="shadow-lg border-0 rounded-3 bg-white py-2" style={{ position: "absolute", top: "115%", left: 0, width: "100%", minWidth: "200px", zIndex: 1000 }}>
          <div className="px-3 py-2 d-flex align-items-center gap-2 dropdown-item-custom" onClick={() => toggleSelection("All")} style={{ cursor: "pointer" }}>
            <input type="checkbox" className="form-check-input" checked={selectedStatuses.includes("All")} readOnly style={{ cursor: "pointer" }} />
            <span className="fw-bold" style={{ fontSize: "0.85rem", color: "#333" }}>Show All</span>
          </div>
          <div className="dropdown-divider my-1"></div>
          {OPTIONS.map((option) => (
            <div key={option} className="px-3 py-2 d-flex align-items-center gap-2 dropdown-item-custom" onClick={(e) => { e.stopPropagation(); toggleSelection(option); }} style={{ cursor: "pointer" }}>
              <input type="checkbox" className="form-check-input" checked={selectedStatuses.includes(option)} readOnly style={{ cursor: "pointer" }} />
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: getStatusColor(option) }}></div>
              <span style={{ fontSize: "0.85rem", fontWeight: "500", color: "#333" }}>{option}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* How the printed report can be grouped. Each type produces a heading and a
   Sub Total line, the way the ERP's supplier-wise report does. "Detailed"
   prints one flat list with a grand total only.

   `value` is only needed where the group is not simply the column: Date Wise
   buckets by day, so the timestamp has to be reduced to a date first or every
   row becomes its own group. */
/* The two ways of reading any of the report types below. */
const SCOPES = [
  { key: "all", label: "All", icon: "bi-collection" },
  { key: "pending", label: "Pending", icon: "bi-hourglass-split" },
];

const REPORT_TYPES = [
  { key: "none", label: "Detailed (No Grouping)", icon: "bi-list-ul", tone: "grey" },
  { key: "ClientName", label: "Client Wise", icon: "bi-building", tone: "blue" },
  { key: "AssigneeName", label: "User Wise", icon: "bi-person-badge", tone: "purple" },
  { key: "Status", label: "Status Wise", icon: "bi-flag", tone: "green" },
  { key: "TeamName", label: "Team Wise", icon: "bi-people", tone: "orange" },
  {
    key: "QueryDate",
    label: "Date Wise",
    icon: "bi-calendar3",
    tone: "pink",
    value: (r) =>
      r.QueryDate ? new Date(r.QueryDate).toLocaleDateString("en-GB") : "(no date)",
  },
  { key: "ProjectName", label: "Project Wise", icon: "bi-diagram-3", tone: "teal" },
  { key: "QueryType", label: "Query Type Wise", icon: "bi-tag", tone: "blue" },
];

/* The same colours as a React style object, for the on-screen report.
   readableOn darkens the text where a badge pair would be too faint at report
   body size — see the note on it in badgeColors. */
const cellTint = (key) => {
  const fn = REPORT_CELL_COLORS[key];
  if (!fn) return undefined;
  return (value, row) => {
    const c = readableOn(fn(value, row));
    if (!c || !c.bg || c.bg === "transparent") return undefined;
    return { backgroundColor: c.bg, color: c.text, fontWeight: 600 };
  };
};

/* The columns worth narrowing on, in the order the report reads in.
 *
 * Same shape as the User Report's list — one entry per column, options read
 * off the loaded rows rather than a master table, so a client with nothing in
 * the window is not offered as a choice. Status is NOT here: this page has
 * carried its own status picker since before the others existed, and it is
 * wired into the export labels and the Pending/All counts. */
const ATTRIBUTE_FILTERS = [
  {
    key: "client",
    label: "Client",
    plural: "clients",
    icon: "bi-building",
    field: "ClientName",
    /* Matches the printed column, which falls back to CompanyName — filtering
       on what the reader can see rather than on what the record stores. */
    value: (r) => r.ClientName || r.CompanyName,
  },
  { key: "project", label: "Project", plural: "projects", icon: "bi-diagram-3", field: "ProjectName" },
  { key: "assignee", label: "Assignee", plural: "assignees", icon: "bi-person-badge", field: "AssigneeName" },
  { key: "team", label: "Team", plural: "teams", icon: "bi-people", field: "TeamName" },
  {
    key: "menu",
    label: "Menu",
    plural: "menus",
    icon: "bi-list-nested",
    field: "MainMenuName",
    value: (r) => r.MainMenuName || r.FormName,
  },
  { key: "querytype", label: "Query Type", plural: "query types", icon: "bi-tag", field: "QueryType" },
  { key: "priority", label: "Priority", plural: "priorities", icon: "bi-flag-fill", field: "Priority" },
];

// --- MAIN REPORTS COMPONENT ---
const Reports = () => {
  const dispatch = useDispatch();
  const { reportListData, excelClientReporData, status } = useSelector(
    (state) => state.report,
  );

  const historyData = useSelector(selectHistoryApiList);
  const historyStatus = useSelector(selectHistoryApiApiListStatus);

  const [printGroupBy, setPrintGroupBy] = useState("TeamName");
  /* All by default: a report that silently hid completed work would be a
     trap the first time somebody printed it for a client. */
  const [reportScope, setReportScope] = useState("all");

  const [statusFilter, setStatusFilter] = useState(["All"]);

  /* Advanced Filters — the multi-condition dialog reached from the funnel in
     the header. It used to live inside the grid this page rendered; the page
     now renders the printable document directly, so it owns the conditions
     itself and hands the same list to the same dialog. */
  const [filterList, setFilterList] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  /* Setup, then report — the same two screens the User Report uses, for the
     same reason: the controls and the document were sharing a height, and the
     document won. */
  const [view, setView] = useState("setup");
  const [showFilters, setShowFilters] = useState(true);
  const [showTypes, setShowTypes] = useState(true);
  const [chipMenu, setChipMenu] = useState(null);
  const chipsRef = useRef(null);

  const [attrFilters, setAttrFilters] = useState(() =>
    Object.fromEntries(ATTRIBUTE_FILTERS.map((f) => [f.key, ["All"]])),
  );
  const setAttrFilter = (key, values) =>
    setAttrFilters((prev) => ({ ...prev, [key]: values }));
  /* This page reads .startDate / .endDate, so the shared window is mapped
     onto its own key names rather than the other way round. */
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = lastSevenDays();
    return { startDate: start, endDate: end };
  });

  /* These three were driven by the data grid: the rows it had narrowed to,
     the columns left visible, and its per-column filters. With the grid gone
     they never change, so they are plain constants rather than state that
     nothing can set. Every reader already falls back when they are empty,
     which is what keeps the export working. */
  const finalExportData = null;
  const visibleColumns = [];
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedHistoryTask, setSelectedHistoryTask] = useState(null);

  const [exportDateRange, setExportDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [localRawData, setLocalRawData] = useState([]);

  const lastFetchedParams = useRef("");

  const windowScrollRef = useRef(0);
  const tableScrollRef = useRef(0);



  const handleRowClick = useCallback((row) => {
    // Save exact scroll positions immediately
    windowScrollRef.current = window.scrollY || document.documentElement.scrollTop;

    const tableWrapper = document.querySelector(".table-responsive")
      || document.querySelector(".rt-tbody")
      || document.querySelector(".report-container table:not(.rv-table)")?.parentElement;

    if (tableWrapper) {
      tableScrollRef.current = tableWrapper.scrollTop;
    }

    setSelectedHistoryTask(row);
    setIsHistoryModalOpen(true);
  }, []);

  const closeHistoryModal = useCallback(() => {
    // 1. Grab the saved coordinates
    const savedWindowY = windowScrollRef.current;
    const savedTableY = tableScrollRef.current;

    // 2. Trigger modal close
    setIsHistoryModalOpen(false);
    setSelectedHistoryTask(null);

    // 3. Create a strict snapping function to kill animations and force position
    const enforceInstantScroll = () => {
      // Temporarily kill global CSS smooth scrolling
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";

      // Snap window instantly
      window.scrollTo({ top: savedWindowY, left: 0, behavior: "auto" });

      // Snap table instantly
      const tableWrapper = document.querySelector(".table-responsive")
        || document.querySelector(".rt-tbody")
        || document.querySelector(".report-container table:not(.rv-table)")?.parentElement;

      if (tableWrapper) {
        tableWrapper.style.scrollBehavior = "auto";
        tableWrapper.scrollTop = savedTableY;
      }

      // Cleanup our manual scroll killing after 10ms
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = "";
        document.body.style.scrollBehavior = "";
        if (tableWrapper) tableWrapper.style.scrollBehavior = "";
      }, 10);
    };

    // 4. Fire the snap instantly, and fire it one more time exactly when the browser repaints
    enforceInstantScroll();
    requestAnimationFrame(enforceInstantScroll);
  }, []);

  const handleExportOpen = useCallback(() => setIsExportModalOpen(true), []);

  /* getRowClassName / handleTableDataChange / handleTableFilterChange /
     handleVisibleColumnsChange were the grid's callbacks. The grid is gone,
     so they are too — see the note by the export for what the export now
     falls back to. */

  const fetchReports = useCallback(
    (start, end) => {
      const payload = {
        FromDate: start ? format(start, "yyyy-MM-dd") : null,
        ToDate: end ? format(end, "yyyy-MM-dd") : null,
      };
      dispatch(reportListsApis(payload));
    },
    [dispatch],
  );

  const fromDateStr = dateRange.startDate ? format(dateRange.startDate, "yyyy-MM-dd") : "";
  const toDateStr = dateRange.endDate ? format(dateRange.endDate, "yyyy-MM-dd") : "";

  /* Pending Only ignores the date range, on purpose.
   *
   * A backlog is not a window. Narrowing "still outstanding" to the last seven
   * days answers a question nobody asks — the task raised in May and never
   * started is exactly the one worth seeing, and exactly the one a date range
   * hides. The pending view fetches all time; sp_Queries_GetAll already reads
   * a NULL date as "no limit" at that end.
   *
   * The scope is part of the fetch key, so switching back to All re-fetches
   * the window instead of keeping the all-time rows on screen. */
  useEffect(() => {
    const allTime = reportScope === "pending";
    const currentParams = allTime ? "ALL-TIME" : `${fromDateStr}|${toDateStr}`;
    if (lastFetchedParams.current === currentParams) return;

    if (allTime) {
      lastFetchedParams.current = currentParams;
      fetchReports(null, null);
      return;
    }

    if (fromDateStr && toDateStr) {
      lastFetchedParams.current = currentParams;
      fetchReports(dateRange.startDate, dateRange.endDate);
    }
  }, [fetchReports, fromDateStr, toDateStr, dateRange.startDate, dateRange.endDate, reportScope]);

  useEffect(() => {
    if (!isExportingExcel) {
      let extracted = [];
      if (Array.isArray(reportListData?.data?.data?.data)) extracted = reportListData.data.data.data;
      else if (Array.isArray(reportListData?.data?.data)) extracted = reportListData.data.data;
      else if (Array.isArray(reportListData?.data)) extracted = reportListData.data;
      else if (Array.isArray(reportListData)) extracted = reportListData;

      setLocalRawData((prev) => {
        if (prev.length !== extracted.length) return extracted;
        if (JSON.stringify(prev) !== JSON.stringify(extracted)) return extracted;
        return prev;
      });
    }
  }, [reportListData, isExportingExcel]);

  useEffect(() => {
    if (selectedHistoryTask && isHistoryModalOpen) {
      dispatch(historyListAPIs({ queryCode: selectedHistoryTask.QueryCode }));
    }
  }, [selectedHistoryTask, isHistoryModalOpen, dispatch]);

  const handleDateChange = (start, end) => {
    const currentStart = dateRange.startDate ? dateRange.startDate.getTime() : 0;
    const currentEnd = dateRange.endDate ? dateRange.endDate.getTime() : 0;
    const newStart = start ? start.getTime() : 0;
    const newEnd = end ? end.getTime() : 0;
    if (currentStart !== newStart || currentEnd !== newEnd) {
      setDateRange({ startDate: start, endDate: end });
    }
  };

  const fmtTotalDuration = (m) => (m ? `${m} min` : "0 min");

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  /* The Status chips in the header. The Advanced Filters dialog narrows this
     further — see filteredData, below printColumns, which is where it has the
     column definitions it needs. */
  /* Which preset button is lit — derived, so typing into the date boxes
     correctly drops the highlight instead of leaving a stale claim. */
  const activePreset = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return null;
    const hit = DATE_PRESETS.find((p) => {
      const r = p.range();
      return isSameDay(r.startDate, startDate) && isSameDay(r.endDate, endDate);
    });
    return hit ? hit.key : null;
  }, [dateRange]);

  const applyPreset = (preset) => {
    const r = preset.range();
    handleDateChange(r.startDate, r.endDate);
  };

  /* Parsed at an explicit midnight: `new Date("2026-08-14")` is read as UTC,
     which lands on the 13th west of Greenwich and shifts the whole report. */
  const setDateEnd = (which) => (e) => {
    const v = e.target.value;
    const parsed = v ? new Date(`${v}T00:00:00`) : null;
    if (!parsed) return;
    handleDateChange(
      which === "start" ? parsed : dateRange.startDate,
      which === "end" ? parsed : dateRange.endDate,
    );
  };

  const dateInputValue = (d) => (d ? format(d, "yyyy-MM-dd") : "");

  const handleCalendarRange = (dates) => {
    const [start, end] = Array.isArray(dates) ? dates : [dates, dates];
    handleDateChange(start, end);
  };

  const clearDates = () => {
    const r = DATE_PRESETS[0].range();
    handleDateChange(r.startDate, r.endDate);
  };

  const rangeLabel = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return "—";
    if (isSameDay(startDate, endDate)) return format(startDate, "dd/MM/yyyy");
    return `${format(startDate, "dd/MM/yyyy")} – ${format(endDate, "dd/MM/yyyy")}`;
  }, [dateRange]);

  const presetLabel = useMemo(() => {
    const hit = DATE_PRESETS.find((p) => p.key === activePreset);
    return hit ? hit.label : "Custom range";
  }, [activePreset]);

  /* mousedown, not click: a menu closing on click-up is still on screen while
     the press lands underneath it. */
  useEffect(() => {
    if (!chipMenu) return undefined;
    const onDown = (e) => {
      if (chipsRef.current && !chipsRef.current.contains(e.target)) {
        setChipMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [chipMenu]);

  const statusFilteredData = useMemo(() => {
    if (statusFilter.includes("All")) return localRawData;
    // Normalize both sides so a casing / spacing difference doesn't silently drop
    // rows whose Status is really a match.
    const targets = statusFilter.map((s) => String(s || "").trim().toLowerCase());
    return localRawData.filter((item) =>
      targets.includes(String(item.Status || "").trim().toLowerCase())
    );
  }, [localRawData, statusFilter]);

  // Summary totals are summed from the rows actually shown (status + any in-table
  // column filter), so the chips always agree with the visible table.
  /* Columns for the printed document. Deliberately not the grid's columns:
     those render JSX with click handlers and 25 of them would never fit an
     A4 width. This is the subset that reads as a report on paper, with plain
     values only.

     EstimateTime is minutes and TimeTaken is seconds — the same units the
     grid's own formatters assume — so the summed columns are kept in minutes
     and the header says so, rather than adding up "2h 30m" strings. */
  /* What each filter can offer, read off the rows that came back. Blanks
     dropped: "(none)" as a choice would filter to rows the report shows as
     empty, which nobody is looking for. */
  const attrOptions = useMemo(() => {
    const out = {};
    ATTRIBUTE_FILTERS.forEach((f) => {
      const seen = new Set();
      localRawData.forEach((r) => {
        const v = f.value ? f.value(r) : r[f.field];
        const text = String(v ?? "").trim();
        if (text) seen.add(text);
      });
      out[f.key] = [...seen].sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [localRawData]);

  /* Applied after the status picker and before the scope switch, so the
     Pending / All counts describe the set the report will actually show.
     Filters left on "All" are skipped entirely. */
  const attrFilteredData = useMemo(() => {
    const active = ATTRIBUTE_FILTERS.filter((f) => {
      const sel = attrFilters[f.key] || [];
      return sel.length > 0 && !sel.includes("All");
    });
    if (!active.length) return statusFilteredData;

    return statusFilteredData.filter((row) =>
      active.every((f) => {
        const v = f.value ? f.value(row) : row[f.field];
        return attrFilters[f.key].includes(String(v ?? "").trim());
      }),
    );
  }, [statusFilteredData, attrFilters]);

  const attrFilterCount = useMemo(
    () =>
      ATTRIBUTE_FILTERS.filter((f) => {
        const sel = attrFilters[f.key] || [];
        return sel.length > 0 && !sel.includes("All");
      }).length,
    [attrFilters],
  );

  const printColumns = useMemo(() => {
    // Same instant, same timezone, on every host — see utils/common.
    const dt = (v) => (v ? toAppTime(v).format("DD/MM/YY HH:mm") : "");

    return [
      /* Widths are real minimums now, not proportions — the sheet grows to
         hold them. Each is set to fit its widest ordinary value on one or two
         lines: "TEXTILE INDUSTRY" needs ~90px, "Not Started" ~70px, a
         "17/08/26 14:30" stamp ~90px. Anything narrower is what produced
         TEXTI / LE / INDUS / TRY. */
      { key: "QueryNo", label: "Query No", width: "52px", value: (r) => r.QueryNo },
      {
        key: "QueryDate",
        label: "Date",
        width: "64px",
        value: (r) => (r.QueryDate ? new Date(r.QueryDate).toLocaleDateString("en-GB") : ""),
        /* `type` is what makes the Advanced Filters dialog offer a date range
           instead of a text box, and `filterDateValue` hands it the stored
           value rather than the formatted one — "17/08/26 14:30" has a
           two-digit year that no date parser can read unambiguously. */
        type: "date",
        filterDateValue: (r) => r.QueryDate,
      },
      { key: "ClientName", label: "Client", width: "92px", value: (r) => r.ClientName || r.CompanyName },
      { key: "ProjectName", label: "Project", width: "100px", value: (r) => r.ProjectName },
      { key: "MainMenuName", label: "Menu", width: "100px", value: (r) => r.MainMenuName || r.FormName },
      { key: "TaskName", label: "Task Name", width: "150px", value: (r) => r.TaskName },
      { key: "NatureofQuery", label: "Nature of Query", width: "190px", value: (r) => r.NatureofQuery },
      /* Ahead of Description, not eleven columns behind it. Description and
         Solution are free text; people write "completed" in them and leave the
         status alone, so a row can read as finished while the field that
         decides whether it is pending says On Hold. Putting the tinted Status
         first means the authoritative value is the one you reach on the way in. */
      {
        key: "Status",
        label: "Status",
        width: "78px",
        value: (r) => displayStatus(r.Status),
        cellStyle: cellTint("Status"),
      },
      { key: "Description", label: "Description", width: "160px", value: (r) => r.Description },
      { key: "Solution", label: "Solution", width: "160px", value: (r) => r.Solution },
      {
        key: "Client_UserName",
        label: "Raised By",
        width: "88px",
        value: (r) => r.Client_UserName,
        /* An open set of client codes, so a hashed tint rather than a fixed
           palette — the same client keeps the same colour across pages, which
           is what lets you see at a glance that six rows in a row came from
           the same place. */
        cellStyle: cellTint("Client_UserName"),
      },
      { key: "MobileNo", label: "Mobile", width: "78px", value: (r) => r.MobileNo },
      { key: "C_UserName", label: "Created By", width: "88px", value: (r) => r.C_UserName },
      { key: "AssignedByName", label: "Assigned By", width: "88px", value: (r) => r.AssignedByName },
      { key: "AssigneeName", label: "Assignee", width: "88px", value: (r) => r.AssigneeName },
      {
        key: "TeamName",
        label: "Team",
        width: "72px",
        value: (r) => r.TeamName,
        // The same colour a team wears everywhere else in the app.
        cellStyle: cellTint("TeamName"),
      },
      {
        key: "QueryType",
        label: "Type",
        width: "64px",
        value: (r) => r.QueryType,
        cellStyle: cellTint("QueryType"),
      },
      {
        key: "isUpdate",
        label: "Is Update",
        width: "80px",
        value: (r) => r.IsUpdate || r.isUpdate,
        cellStyle: cellTint("isUpdate"),
      },
      {
        key: "Priority",
        label: "Priority",
        width: "62px",
        value: (r) => r.Priority,
        cellStyle: cellTint("Priority"),
      },
      {
        key: "isManuallyRaised",
        label: "Manual",
        width: "54px",
        value: (r) => (r.isManuallyRaised ? "Yes" : "No"),
        // Only "Yes" is tinted — see getYesNoColors.
        cellStyle: cellTint("isManuallyRaised"),
      },
      {
        key: "IsSupportTeam",
        label: "Support",
        width: "58px",
        value: (r) => (r.IsSupportTeam ? "Yes" : "No"),
        cellStyle: cellTint("IsSupportTeam"),
      },
      { key: "AssignedTime", label: "Assigned On", width: "94px", value: (r) => dt(r.AssignedTime), type: "datetime", filterDateValue: (r) => r.AssignedTime },
      { key: "StartTime", label: "Start Time", width: "94px", value: (r) => dt(r.StartTime), type: "datetime", filterDateValue: (r) => r.StartTime },
      { key: "EndTime", label: "End Time", width: "94px", value: (r) => dt(r.EndTime), type: "datetime", filterDateValue: (r) => r.EndTime },
      /* Kept numeric so they can be summed. The grid's "2h 30m" and "HH:MM"
         strings are for reading, not adding up. */
      {
        key: "EstimateTime",
        label: "Est. Time",
        width: "60px",
        total: true,
        kind: "duration",
        value: (r) => r.EstimateTime || 0,
      },
      {
        key: "TimeTakenMins",
        label: "Time Taken",
        width: "64px",
        total: true,
        kind: "duration",
        value: (r) => Math.round((r.TimeTaken || 0) / 60),
      },
      {
        /* Estimate minus actual: positive came in under, negative overran.
           Same definition as the User Report's column of the same name, so
           the two reports cannot tell different stories about one task.

           TimeTaken is SECONDS here where the User Report carries minutes,
           so it is converted before subtracting — the one place these two
           reports could quietly disagree by a factor of sixty. */
        key: "DiffTime",
        label: "Different Time",
        width: "70px",
        total: true,
        kind: "duration",
        value: (r) =>
          (Number(r.EstimateTime) || 0) -
          Math.round((Number(r.TimeTaken) || 0) / 60),
      },
    ];
  }, []);

  /* What the Advanced Filters dialog offers, and what it matches against.

     Built from the report's own columns rather than the grid's, for two
     reasons: these are the columns actually on the page, and their `value`
     accessor becomes `searchValue`, so a condition matches the text the reader
     can see. Filtering Client on "RICE PROWIN" has to find the row whose cell
     reads RICE PROWIN — the underlying record stores that in ClientName on
     some rows and CompanyName on others, and only the accessor knows which. */
  const filterColumns = useMemo(
    () =>
      printColumns.map((c) => ({
        key: c.key,
        label: c.label,
        type: c.type,
        searchValue: c.value,
        filterDateValue: c.filterDateValue,
      })),
    [printColumns],
  );

  /* Status chips first, then the dialog's conditions. Everything downstream —
     the document, the totals chips, both exports — reads this, so the report
     on screen and the file you export from it always agree. */
  /* The Pending / All switch, applied before the dialog's conditions so the
     counts on the switch itself describe the same set the report will show. */
  const scopedData = useMemo(
    () =>
      reportScope === "pending"
        ? attrFilteredData.filter((row) => isPendingStatus(row.Status))
        : attrFilteredData,
    [attrFilteredData, reportScope],
  );

  /* Shown on the Pending / All toggle, so the split is visible before you
     switch rather than only after. */
  const scopeCounts = useMemo(
    () => ({
      total: attrFilteredData.length,
      pending: attrFilteredData.filter((row) => isPendingStatus(row.Status)).length,
    }),
    [attrFilteredData],
  );

  const filteredData = useMemo(
    () =>
      filterList.length === 0
        ? scopedData
        : scopedData.filter((row) =>
            matchesAdvancedFilters(row, filterList, filterColumns),
          ),
    [scopedData, filterList, filterColumns],
  );

  /* Conditions that are actually narrowing anything. A half-filled row — a
     column picked but no value typed yet — matches everything, so counting it
     would put a "2" on the funnel over a report nothing had been done to. */
  const activeFilterCount = useMemo(
    () =>
      filterList.filter((f) =>
        f.startDate || f.endDate ? true : String(f.value || "").trim() !== "",
      ).length,
    [filterList],
  );

  /* Everything narrowing the report on one badge: the column pickers, the
     status picker, and whatever the advanced dialog holds. Three separate
     counts on one funnel would be three answers to one question. */
  const totalFilterCount =
    attrFilterCount +
    (statusFilter.includes("All") || statusFilter.length === 0 ? 0 : 1) +
    activeFilterCount;

  const activePrintGroup = useMemo(
    () => REPORT_TYPES.find((g) => g.key === printGroupBy) ?? REPORT_TYPES[0],
    [printGroupBy],
  );

  const totals = useMemo(() => {
    const rows = finalExportData !== null ? finalExportData : filteredData;
    let totalEst = 0;
    let totalTaken = 0;
    (rows || []).forEach((row) => {
      if (row.EstimateTime) totalEst += Number(row.EstimateTime) || 0;
      const durationObj = calculateDuration(row.StartTime, row.EndTime);
      if (durationObj && durationObj.totalMinutes) totalTaken += durationObj.totalMinutes;
    });
    return { totalEst, totalTaken };
  }, [finalExportData, filteredData]);

  // --- COLUMNS ---
  // --- COLUMNS ---
  const columns = useMemo(
    () => [
      {
        label: "Query No",
        key: "QueryNo",
        width: "110px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            <span className="fw-bold text-secondary">#{r.QueryNo}</span>
          </div>
        ),
      },
      {
        label: "Date",
        key: "QueryDate",
        width: "110px",
        type: "date",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.QueryDate ? new Date(r.QueryDate).toLocaleDateString("en-GB") : "-"}
          </div>
        ),
        filterDateValue: (r) => {
          if (!r.QueryDate) return null;
          return toDateValue(r.QueryDate);
        },
      },
      {
        label: "Client",
        key: "ClientName",
        width: "130px",
        sortValue: (r) => r.ClientName || r.CompanyName || "",
        searchValue: (r) => r.ClientName || r.CompanyName || "",
        render: (r) => (
          <div className="fw-bold text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.ClientName || r.CompanyName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.ClientName || r.CompanyName || <span className="text-muted fst-italic">N/A</span>}
          </div>
        ),
      },
      {
        label: "Project",
        key: "ProjectName",
        width: "150px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.ProjectName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.ProjectName || "-"}
          </div>
        )
      },
      {
        label: "Task Name",
        key: "TaskName",
        width: "250px",
        render: (r) => (
          <div className="text-primary fw-medium text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.TaskName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.TaskName || "-"}
          </div>
        ),
      },
      {
        label: "Nature of Query",
        key: "NatureofQuery",
        width: "250px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.NatureofQuery} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.NatureofQuery || "-"}
          </div>
        )
      },
      {
        label: "Raised By",
        key: "Client_UserName",
        width: "120px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.Client_UserName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.Client_UserName || "-"}
          </div>
        )
      },
      {
        label: "Created UserName",
        key: "C_UserName",
        width: "180px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.C_UserName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.C_UserName || "-"}
          </div>
        )
      },
      {
        label: "Assigned By Name",
        key: "AssignedByName",
        width: "180px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.AssignedByName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.AssignedByName || "-"}
          </div>
        )
      },
      {
        label: "Assignee Name",
        key: "AssigneeName",
        width: "180px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.AssigneeName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.AssigneeName || "-"}
          </div>
        )
      },
      {
        label: "Mobile",
        key: "MobileNo",
        width: "120px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.MobileNo ? <a href={`tel:${r.MobileNo}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>{r.MobileNo}</a> : "-"}
          </div>
        ),
      },
      {
        label: "Manual",
        key: "isManuallyRaised",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.isManuallyRaised ? <span className="badge bg-info text-dark">Yes</span> : <span className="badge bg-light text-secondary border">No</span>}
          </div>
        ),
      },
      {
        label: "Support Team",
        key: "IsSupportTeam",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.IsSupportTeam ? <span className="badge bg-info text-dark">Yes</span> : <span className="badge bg-light text-secondary border">No</span>}
          </div>
        ),
      },
      {
        label: "Query Type",
        key: "QueryType",
        width: "150px",
        render: (r) => {
          let badgeClass = "bg-secondary";
          const type = r.QueryType ? r.QueryType.trim() : "";
          if (type === "Bug" || type === "BUG") badgeClass = "bg-danger";
          else if (type === "New Task") badgeClass = "bg-primary";
          else if (type === "Rework") badgeClass = "bg-warning text-dark";
          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              <span className={`badge ${badgeClass} rounded-pill`} style={{ padding: "6px 12px", letterSpacing: "0.5px" }}>{type || "-"}</span>
            </div>
          );
        },
      },
      {
        label: "Is Update",
        key: "IsUpdate",
        type: "select",
        filterType: "select",
        options: ["None", "Backend work", "Update", "Not update"],
        searchValue: (r) => r.IsUpdate || r.isUpdate || "None",
        render: (r) => {
          const val = r.IsUpdate || r.isUpdate || "None";
          const lowerVal = String(val).toLowerCase();
          let bg = "#f8f9fa"; let color = "#6c757d";
          if (lowerVal === "backend work") { bg = "#f3e5f5"; color = "#7b1fa2"; }
          else if (lowerVal === "update") { bg = "#e8f5e9"; color = "#2e7d32"; }
          else if (lowerVal === "not update") { bg = "#fff3e0"; color = "#e65100"; }
          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              <span className="badge" style={{ backgroundColor: bg, color: color, padding: "6px 10px", borderRadius: "12px", fontSize: "0.80rem" }}>{val}</span>
            </div>
          );
        },
      },
      {
        label: "Priority",
        key: "Priority",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            <span className={`badge ${r.Priority === "High" ? "bg-danger" : "bg-primary"}`}>{r.Priority}</span>
          </div>
        ),
      },
      {
        label: "Est. Time",
        key: "EstimateTime",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.EstimateTime ? `${Math.floor(r.EstimateTime / 60)}h ${r.EstimateTime % 60}m` : "-"}
          </div>
        ),
        exportFormatter: (r) => formatToHHMM(r.EstimateTime),
      },
      {
        label: "Start Time",
        key: "StartTime",
        width: "150px",
        type: "datetime",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {formatDateTimeDisplay(r.StartTime)}
          </div>
        ),
        filterDateValue: (r) => {
          if (!r.StartTime) return null;
          return toDateValue(r.StartTime);
        },
      },
      {
        label: "End Time",
        key: "EndTime",
        width: "150px",
        type: "datetime",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {formatDateTimeDisplay(r.EndTime)}
          </div>
        ),
        filterDateValue: (r) => {
          if (!r.EndTime) return null;
          return toDateValue(r.EndTime);
        },
      },
      {
        label: "Time Taken",
        key: "TimeTaken",
        width: "100px",
        render: (r) => {
          const durationObj = secondsToHoursMinutes(r.TimeTaken);
          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              {durationObj ? durationObj : "-"}
            </div>
          );
        },
        exportFormatter: (r) => {
          const durationStr = secondsToHoursMinutes(r.TimeTaken);
          return durationStr ? durationStr : "-";
        },
      },
      {
        label: "Description",
        key: "Description",
        width: "200px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.Description} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.Description || "-"}
          </div>
        )
      },
      {
        label: "Status",
        key: "Status",
        width: "120px",
        type: "select",
        filterType: "select",
        options: ["Complete", "In Progress", "On Hold", "Pending", "Not Started", "Testing"],
        render: (r) => {
          let badgeClass = "bg-secondary";
          const status = r.Status ? r.Status.trim() : "";
          if (status === "Complete" || status === "Completed") badgeClass = "bg-success";
          else if (status === "In Progress") badgeClass = "bg-primary";
          else if (status === "On Hold") badgeClass = "bg-warning text-dark";
          else if (status === "Pending") badgeClass = "bg-info text-dark";
          else if (status === "Testing") badgeClass = "bg-info text-dark";
          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              <span className={`badge ${badgeClass}`} style={{ padding: "6px 10px", borderRadius: "12px" }}>{displayStatus(r.Status)}</span>
            </div>
          );
        },
      },
      {
        label: "Menu",
        key: "SubMenuName",
        width: "140px",
        sortValue: (r) => r.SubMenuName || r.MainMenuName || r.FormName || "",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.SubMenuName || r.MainMenuName || r.FormName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.SubMenuName || r.MainMenuName || r.FormName || "-"}
          </div>
        ),
      },
      {
        label: "Solution",
        key: "Solution",
        width: "200px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.Solution} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.Solution || "-"}
          </div>
        )
      },
      {
        label: "Assigned On",
        key: "AssignedTime",
        width: "150px",
        type: "datetime",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.AssignedTime ? new Date(r.AssignedTime).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
          </div>
        ),
        filterDateValue: (r) => {
          if (!r.AssignedTime) return null;
          return toDateValue(r.AssignedTime);
        },
      },
    ],
    [handleRowClick],
  );

  /* The data grid that used to be rendered here is gone — this page is the
     report document now. Its three callbacks fed the export: the grid's own
     filtered rows, its visible columns, and its per-column filters.

     Every one of those already had a fallback, which is why removing the
     grid does not break the export:

       rows    -> filteredData        (the date range + status filter)
       columns -> the full column set
       filters -> none listed on the PDF

     So Export now sends everything matching the page filters rather than
     whatever the grid had been narrowed to. `columns` is still needed for
     that fallback, so it stays. */

  /* --- EXCEL EXPORT (the daily management workbook) ---
     The fetching and the shape-juggling now live in utils/supportTeamReport,
     because the User Report offers the same download and two copies of this
     would eventually disagree about what is in the file. */
  const handleExportExcelWithAPI = async () => {
    setIsExportingExcel(true);
    try {
      const res = await downloadSupportTeamReport({
        dispatch,
        dateRange: exportDateRange,
        // Rows already in the store, used only if the count call comes back
        // empty — the behaviour this handler always had.
        fallbackClientCounts: excelClientReporData,
      });

      if (res.ok) setIsExportModalOpen(false);
      else alert(res.reason);

      /* Put the page's own date range back on the wire. The export asks for a
         window of its own, and that response lands in the same slice, so
         without this the report on screen would be left showing whatever the
         export happened to ask for. */
      await dispatch(
        reportListsApis({
          FromDate: dateRange.startDate ? format(dateRange.startDate, "yyyy-MM-dd") : null,
          ToDate: dateRange.endDate ? format(dateRange.endDate, "yyyy-MM-dd") : null,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to fetch export data:", error);
      alert("Error generating the Support Team Report.");
    } finally {
      setIsExportingExcel(false);
    }
  };

  // --- PDF EXPORT ---
  const handleExportPDF = async () => {
    const dataToExport = finalExportData !== null ? finalExportData : filteredData;
    const columnsToExport = visibleColumns && visibleColumns.length > 0 ? visibleColumns : columns;

    if (dataToExport.length === 0) {
      alert("No data available to export.");
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    for (let i = 0; i <= 90; i += 10) {
      setDownloadProgress(i);
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    /* Column widths were measured off the on-screen grid so the PDF matched
       what the user saw. The grid is no longer rendered, and the selector
       must NOT fall through to the report viewer's table: that one has 12
       columns against the export's 25, so its widths would be mapped onto
       the wrong columns by index. Excluding it leaves liveWidths empty, and
       exportToPDF falls back to each column's own width. */
    const liveWidths = {};
    const tableEl = document.querySelector(
      ".report-container table:not(.rv-table)",
    );
    if (tableEl) {
      const thElements = tableEl.querySelectorAll("th");
      if (thElements.length >= columnsToExport.length) {
        columnsToExport.forEach((col, index) => {
          if (thElements[index]) liveWidths[col.key] = thElements[index].getBoundingClientRect().width;
        });
      }
    }

    let totalEst = 0;
    let totalTakenSeconds = 0;

    dataToExport.forEach((row) => {
      if (row.EstimateTime) totalEst += Number(row.EstimateTime) || 0;
      if (row.TimeTaken) totalTakenSeconds += Number(row.TimeTaken) || 0;
    });

    const totalTakenMinutes = Math.floor(totalTakenSeconds / 60);

    const formattedData = dataToExport.map((row) => ({
      ...row,
      StartTime: formatDateTimeDisplay(row.StartTime),
      EndTime: formatDateTimeDisplay(row.EndTime),
    }));

    const footerMap = {
      Priority: "TOTAL",
      EstimateTime: formatToHHMM(totalEst),
      TimeTaken: formatToHHMM(totalTakenMinutes),
    };

    const activeFiltersList = [];
    if (!statusFilter.includes("All")) activeFiltersList.push(`Status: ${statusFilter.join(", ")}`);

    /* The conditions from the Advanced Filters dialog, printed under the
       letterhead. A filtered report that does not say so is the one thing
       worse than no filtering at all — somebody reads 40 rows as if they were
       all of them. This was reading `tableFilters`, an empty array left behind
       when the grid was removed, so the line was always blank. */
    if (filterList.length > 0) {
      filterList.forEach((f) => {
        if (f.value || f.startDate || f.endDate) {
          const col = filterColumns.find((c) => c.key === f.column);
          const label = col ? col.label : f.column;
          if (f.operator === "between") {
            const sDate = f.startDate ? format(new Date(f.startDate), "dd-MMM-yyyy") : "";
            const eDate = f.endDate ? format(new Date(f.endDate), "dd-MMM-yyyy") : "";
            if (sDate && eDate) activeFiltersList.push(`${label}: ${sDate} to ${eDate}`);
            else if (sDate) activeFiltersList.push(`${label}: From ${sDate}`);
            else if (eDate) activeFiltersList.push(`${label}: To ${eDate}`);
          } else if (f.value) {
            activeFiltersList.push(`${label}: ${f.value}`);
          }
        }
      });
    }

    try {
      exportToPDF(
        formattedData,
        columnsToExport,
        "Detailed Query Report",
        dateRange,
        footerMap,
        liveWidths,
        activeFiltersList,
        // Same colours as the on-screen report — see REPORT_CELL_COLORS.
        REPORT_CELL_COLORS,
      );
      setDownloadProgress(100);
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 800);
    } catch (error) {
      console.error("Export failed", error);
      setIsDownloading(false);
      alert("Export failed");
    }
  };

  return (
    <div className="report-container p-2 p-md-4">
      {/* <div className="bg-white rounded-4 shadow-sm p-3 mb-4 border">
        <div className="d-flex d-lg-none flex-column gap-3">
          <h4 className="report-title">Detailed Query Report</h4>
          <div className="d-flex flex-column gap-2">
            <StatusFilterDropdown selectedStatuses={statusFilter} onChange={setStatusFilter} className="w-100" />
            <div className="d-flex gap-2">
              <div className="d-flex align-items-center justify-content-center bg-light px-3 py-2 rounded-3 border flex-grow-1 flex-column">
                <span className="text-muted small fw-bold" style={{ fontSize: "0.65rem" }}>RECORDS</span>
                <span className="badge bg-primary text-white rounded-pill">{finalExportData !== null ? finalExportData.length : filteredData.length}</span>
              </div>
              <div className="d-flex align-items-center justify-content-center bg-light px-3 py-2 rounded-3 border flex-grow-1 flex-column">
                <span className="text-muted small fw-bold" style={{ fontSize: "0.65rem" }}>TOT. EST</span>
                <span className="badge bg-info text-dark rounded-pill">{fmtTotalDuration(totals.totalEst)}</span>
              </div>
              <div className="d-flex align-items-center justify-content-center bg-light px-3 py-2 rounded-3 border flex-grow-1 flex-column">
                <span className="text-muted small fw-bold" style={{ fontSize: "0.65rem" }}>TOT. TAKEN</span>
                <span className="badge bg-success text-white rounded-pill">{fmtTotalDuration(totals.totalTaken)}</span>
              </div>
            </div>
          </div>
          <div className="w-100">
            <div className="w-100" style={{ minHeight: "40px" }}>
              <DateRangeFilter startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={handleDateChange} />
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light border shadow-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2" onClick={() => window.location.reload()} style={{ height: "40px" }}>
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </button>
            <button className="btn btn-primary shadow-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center gap-2" onClick={handleExportOpen} style={{ height: "40px" }}>
              <i className="bi bi-box-arrow-up"></i> Export
            </button>
          </div>
        </div>

        <div className="d-none d-lg-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <h5 className="report-title">Detailed Query Report</h5>
            <div className="d-flex align-items-center bg-light px-3 py-1 rounded-pill border">
              <span className="text-muted small fw-bold me-2" style={{ fontSize: "0.7rem" }}>RECORDS</span>
              <span className="badge bg-primary text-white rounded-pill">{finalExportData !== null ? finalExportData.length : filteredData.length}</span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <StatusFilterDropdown selectedStatuses={statusFilter} onChange={setStatusFilter} />
            <DateRangeFilter startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={handleDateChange} />
            <div className="vr mx-2 text-muted h-50"></div>
            <button className="btn btn-light border shadow-sm" onClick={() => window.location.reload()} title="Refresh">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
            <button className="btn btn-primary shadow-sm fw-bold d-flex align-items-center gap-2" onClick={handleExportOpen}>
             <i className="bi bi-box-arrow-up"></i> Export
            </button>
          </div>
        </div>
      </div> */}


      {/* Identity and totals only. Everything that DECIDES the report
          moved to the setup screen below, where each control has a label
          and room to be read. Hidden once the document is open — the
          context bar names it from there. */}
      {view === "setup" && (
        <PageHeader
          title="Detailed Query Report"
          /* Same totals table at the back. Client is this report's own
             subject the way person is the User Report's. */
          appendSummary
          /* Two queues, one report. A task raised through the Common Team
             screen carries IsSupportTeam; everything else is the tech team's
             own work. The summary counted them together, which is the one
             split the two queues exist to make. */
          summarySegments={[
            {
              key: "common",
              label: "Common Task",
              test: (r) => Boolean(r.IsSupportTeam),
            },
            {
              key: "tech",
              label: "Tech Team",
              test: (r) => !r.IsSupportTeam,
            },
          ]}
          summaryBy={{
            key: "ClientName",
            label: "Client",
            value: (r) => r.ClientName || r.CompanyName || "(none)",
          }}
          subtitle="View, filter and export reports."
          icon={<i className="bi bi-file-earmark-bar-graph-fill fs-4 text-primary"></i>}
          // actions={
          //   <div className="d-flex align-items-center gap-2 flex-wrap">

          //     <div className="d-flex align-items-center bg-light px-3 py-1 rounded-pill border">
          //       <span
          //         className="text-muted small fw-bold me-2"
          //         style={{ fontSize: "0.7rem" }}
          //       >
          //         RECORDS
          //       </span>

          //       <span className="badge bg-primary text-white rounded-pill">
          //         {finalExportData !== null
          //           ? finalExportData.length
          //           : filteredData.length}
          //       </span>
          //     </div>

          //     <StatusFilterDropdown
          //       selectedStatuses={statusFilter}
          //       onChange={setStatusFilter}
          //     />

          //     <DateRangeFilter
          //       startDate={dateRange.startDate}
          //       endDate={dateRange.endDate}
          //       onChange={handleDateChange}
          //     />

          //     <button
          //       className="btn btn-light border shadow-sm"
          //       onClick={() => window.location.reload()}
          //     >
          //       <i className="bi bi-arrow-clockwise"></i>
          //     </button>

          //     <button
          //       className="btn btn-primary d-flex align-items-center gap-2"
          //       onClick={handleExportOpen}
          //     >
          //       <i className="bi bi-box-arrow-up"></i>
          //       Export
          //     </button>
          //   </div>
          // }
          actions={
            <div className="d-flex align-items-center gap-2 flex-wrap">

              {/* RECORDS */}
              {/* <div
                className="d-flex align-items-center px-3 py-2 rounded-3 shadow-sm"
                style={{
                  backgroundColor: "#2563EB",
                  minHeight: "40px",
                }}
              >
                <span
                  className="small fw-bold me-2"
                  style={{
                    fontSize: "0.7rem",
                    letterSpacing: "0.5px",
                    color: "#FFFFFF",
                  }}
                >
                  RECORDS
                </span>

                <span
                  className="fw-bold"
                  style={{
                    fontSize: "0.9rem",
                    color: "#FFFFFF",
                  }}
                >
                  {finalExportData !== null
                    ? finalExportData.length
                    : filteredData.length}
                </span>
              </div> */}

              {/* Records Card */}
              <StatChip
                icon="bi-clipboard-data-fill"
                tone="blue"
                label="Records"
                value={
                  finalExportData !== null
                    ? finalExportData.length
                    : filteredData.length
                }
              />

              {/* Total time taken across the shown rows */}
              <StatChip
                icon="bi-briefcase-fill"
                tone="green"
                label="Time Taken"
                value={formatToHHMM(totals.totalTaken)}
              />

              {/* Total estimated time across the shown rows */}
              <StatChip
                icon="bi-hourglass-split"
                tone="orange"
                label="Est. Time"
                value={formatToHHMM(totals.totalEst)}
              />

              {/* REFRESH */}
              <button
                className="btn btn-light border shadow-sm"
                onClick={() => window.location.reload()}
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>

              {/* EXPORT */}
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleExportOpen}
              >
                <i className="bi bi-box-arrow-up"></i>
             
              </button>

            </div>
          }
        />
      )}


      {/* The same dialog the grid used, driven from this page's own state.
          Suggestions in the Value box come from the status-filtered rows, so
          it offers the values that are actually on this report. */}
      {showFilterPanel && (
        <FilterPanel
          filterList={filterList}
          setFilterList={setFilterList}
          onClose={() => setShowFilterPanel(false)}
          onClearAll={() => setFilterList([])}
          columns={filterColumns}
          data={statusFilteredData}
        />
      )}



      {/* ---------------------------------------------------------------
          SETUP — what the report is going to be.
          --------------------------------------------------------------- */}
      {view === "setup" && (
        <div className="rsx">
          <section className="rsx__card">
            <div className="rsx__head">
              <span className="rsx__legend">Date Range</span>
            </div>

            <div className="rsx__dates">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`rsx__preset${
                    activePreset === preset.key ? " is-active" : ""
                  }`}
                  onClick={() => applyPreset(preset)}
                >
                  <i className={`bi ${preset.icon}`} />
                  {preset.label}
                </button>
              ))}

              <span className="rsx__dategap" />

              <input
                type="date"
                className="rsx__date"
                value={dateInputValue(dateRange.startDate)}
                onChange={setDateEnd("start")}
                aria-label="From date"
              />
              <span className="rsx__to">to</span>
              <input
                type="date"
                className="rsx__date"
                value={dateInputValue(dateRange.endDate)}
                onChange={setDateEnd("end")}
                aria-label="To date"
              />
            </div>
          </section>

          <div className="rsx__cols">
            <section className="rsx__card">
              <div className="rsx__head">
                <span className="rsx__legend">Filters</span>
                <button
                  type="button"
                  className="rsx__hide"
                  onClick={() => setShowFilters((v) => !v)}
                >
                  {showFilters ? "Hide" : "Show"}
                  <i className={`bi bi-chevron-${showFilters ? "up" : "down"}`} />
                </button>
              </div>

              {showFilters && (
                <div className="rsx__body">
                  {ATTRIBUTE_FILTERS.map((f) => (
                    <div className="rsx__field" key={f.key}>
                      <span className="rsx__label">{f.label}</span>
                      <ValueFilterDropdown
                        icon={f.icon}
                        label={f.label}
                        plural={f.plural}
                        options={attrOptions[f.key] || []}
                        selected={attrFilters[f.key] || ["All"]}
                        onChange={(v) => setAttrFilter(f.key, v)}
                      />
                    </div>
                  ))}

                  {/* This page's own status picker, kept rather than replaced:
                      it is wired into the export labels and it offers the
                      statuses the app knows about, not only the ones that
                      happen to be in the window. */}
                  <div className="rsx__field">
                    <span className="rsx__label">Status</span>
                    <StatusFilterDropdown
                      selectedStatuses={statusFilter}
                      onChange={setStatusFilter}
                      className="w-100"
                    />
                  </div>

                  <div className="rsx__field">
                    <span className="rsx__label">Advanced</span>
                    <button
                      type="button"
                      className={`rsx__control rsx__filterbtn${
                        activeFilterCount ? " is-on" : ""
                      }`}
                      onClick={() => setShowFilterPanel(true)}
                    >
                      <i
                        className={`bi ${
                          activeFilterCount ? "bi-funnel-fill" : "bi-funnel"
                        }`}
                      />
                      {activeFilterCount
                        ? `${activeFilterCount} filter${
                            activeFilterCount === 1 ? "" : "s"
                          } applied`
                        : "Add column filters…"}
                      {activeFilterCount > 0 && (
                        <span className="rsx__count">{activeFilterCount}</span>
                      )}
                    </button>
                  </div>

                  <p className="rsx__note">
                    Filters narrow the rows the report is built from — the
                    document is generated from whatever is left.
                  </p>
                </div>
              )}
            </section>

            {/* The scope bar and the type cards are one grid item, not two.
                .rsx__cols is a two-column grid, so a loose sibling here takes
                the right column and shunts the cards it belongs to onto the
                next row, underneath the filters. */}
            <div className="rsx__col">
            {/* Which rows the report is built from — All work, or only what
                is still outstanding.
                *
                * Promoted out of the filter list and given its own bar because
                * it is not the same kind of choice as Client or Priority. Those
                * narrow a report; this one decides which report it is, and
                * every type below obeys it. Buried in a select among nine
                * others it read as one more optional filter. */}
            <div className="rsx__scope" role="group" aria-label="Report scope">
              <span className="rsx__scope-label">Show</span>

              <div className="rsx__scope-set">
                {SCOPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`rsx__scope-btn${
                      reportScope === s.key ? " is-active" : ""
                    }`}
                    onClick={() => setReportScope(s.key)}
                    aria-pressed={reportScope === s.key}
                  >
                    <i className={`bi ${s.icon}`} aria-hidden="true" />
                    {s.label}
                    <span className="rsx__scope-count">
                      {s.key === "pending"
                        ? scopeCounts.pending
                        : scopeCounts.total}
                    </span>
                  </button>
                ))}
              </div>

              {/* Pending deliberately ignores the dates above — a backlog cut
                  to this week is not a backlog. Said here rather than left for
                  somebody to notice from the row count. */}
              {reportScope === "pending" && (
                <span className="rsx__scope-note">
                  <i className="bi bi-info-circle" aria-hidden="true" />
                  All time — the date range above does not apply
                </span>
              )}
            </div>

            <section className="rsx__card">
              <div className="rsx__head">
                <span className="rsx__legend">Report Type</span>
                <button
                  type="button"
                  className="rsx__hide"
                  onClick={() => setShowTypes((v) => !v)}
                >
                  {showTypes ? "Hide" : "Show"}
                  <i className={`bi bi-chevron-${showTypes ? "up" : "down"}`} />
                </button>
              </div>

              {showTypes && (
                <div className="rsx__body">
                  <div className="rsx__types">
                    {REPORT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`rsx__type${
                          printGroupBy === t.key ? " is-active" : ""
                        }`}
                        /* Choosing the type opens the report — the Generate
                           button it replaces asked for the same decision
                           twice. */
                        onClick={() => {
                          setPrintGroupBy(t.key);
                          setView("report");
                        }}
                        aria-pressed={printGroupBy === t.key}
                      >
                        <span
                          className={`rsx__type-icon rsx__type-icon--${
                            t.tone || "blue"
                          }`}
                        >
                          <i className={`bi ${t.icon}`} />
                        </span>
                        <span className="rsx__type-label">{t.label}</span>
                      </button>
                    ))}

                    {/* Produces a file rather than changing the report, so it
                        never shows as selected. */}
                    <button
                      type="button"
                      className="rsx__type"
                      onClick={handleExportOpen}
                    >
                      <span className="rsx__type-icon rsx__type-icon--green">
                        <i className="bi bi-file-earmark-spreadsheet" />
                      </span>
                      <span className="rsx__type-label">Client Excel Export</span>
                    </button>
                  </div>
                </div>
              )}
            </section>
            </div>
          </div>

          <div className="rsx__go">
            <span className="rsx__ready">
              {status === "loading" ? (
                "Loading rows…"
              ) : filteredData.length === 0 ? (
                <>
                  <i className="bi bi-exclamation-circle me-1" />
                  Nothing matches these filters — nothing to report on yet.
                </>
              ) : (
                <>
                  <i className="bi bi-hand-index-thumb me-1" />
                  Choose a report type above to open it ·{" "}
                  <strong>{filteredData.length}</strong> rows ready
                </>
              )}
            </span>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------
          REPORT — kept mounted and hidden, so the page you were on, the zoom
          and the columns you switched off survive a trip back to setup.
          --------------------------------------------------------------- */}
      <div
        className="position-relative"
        style={{
          minHeight: "300px",
          display: view === "report" ? undefined : "none",
        }}
      >

        {status === "loading" && !isExportingExcel && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 99, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "16px" }}>
            <Loader isLoading={true} />
          </div>
        )}

        {status === "failed" && !isExportingExcel && (
          <div className="alert alert-danger m-0 mb-3">
            Error loading reports.
          </div>
        )}

        <ReportViewer
          show
          embedded
          visible={view === "report"}
          splitChrome
          onClose={() => setView("setup")}
          backLabel="Back"
          contextLeft={
            <div className="rv-chips" ref={chipsRef}>
              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-datechip${chipMenu === "date" ? " is-open" : ""}`}
                  onClick={() =>
                    setChipMenu((m) => (m === "date" ? null : "date"))
                  }
                  title="Change the date range"
                >
                  <i className="bi bi-calendar3" />
                  <span className="rv-datechip__text">
                    <strong>{presetLabel}</strong>
                    <em>{rangeLabel}</em>
                  </span>
                </button>

                {chipMenu === "date" && (
                  <div className="rv-datemenu">
                    <div className="rv-datemenu__body">
                      <div className="rv-datemenu__presets">
                        {DATE_PRESETS.map((preset) => (
                          <button
                            key={preset.key}
                            type="button"
                            className={`rv-datemenu__preset${
                              activePreset === preset.key ? " is-active" : ""
                            }`}
                            onClick={() => applyPreset(preset)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="rv-datemenu__cal">
                        <DatePicker
                          selected={dateRange.startDate}
                          startDate={dateRange.startDate}
                          endDate={dateRange.endDate}
                          onChange={handleCalendarRange}
                          selectsRange
                          inline
                          monthsShown={1}
                          calendarStartDay={1}
                        />

                        <div className="rv-datemenu__inputs">
                          <input
                            type="date"
                            value={dateInputValue(dateRange.startDate)}
                            onChange={setDateEnd("start")}
                            aria-label="From date"
                          />
                          <span>&rarr;</span>
                          <input
                            type="date"
                            value={dateInputValue(dateRange.endDate)}
                            onChange={setDateEnd("end")}
                            aria-label="To date"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rv-datemenu__foot">
                      <button
                        type="button"
                        className="rv-datemenu__clear"
                        onClick={clearDates}
                        title="Back to the default window"
                      >
                        Clear
                      </button>
                      <span className="rv-datemenu__hint">
                        <i className="bi bi-check-circle" />
                        Click outside to apply
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-chip${chipMenu === "type" ? " is-open" : ""}`}
                  onClick={() =>
                    setChipMenu((m) => (m === "type" ? null : "type"))
                  }
                  title="Change the report type"
                >
                  <span className="rv-chip__tile">
                    <i className={`bi ${activePrintGroup.icon}`} />
                  </span>
                  {activePrintGroup.label}
                  <i className="bi bi-caret-down-fill" style={{ fontSize: "0.6rem" }} />
                </button>

                {chipMenu === "type" && (
                  <div className="rv-typemenu">
                    {REPORT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`rv-typemenu__item${
                          printGroupBy === t.key ? " is-active" : ""
                        }`}
                        onClick={() => {
                          setPrintGroupBy(t.key);
                          setChipMenu(null);
                        }}
                      >
                        <span className={`rv-typemenu__tile rv-typemenu__tile--${t.tone || "blue"}`}>
                          <i className={`bi ${t.icon}`} />
                        </span>
                        <span className="rv-typemenu__label">{t.label}</span>
                        {printGroupBy === t.key && (
                          <i className="bi bi-check2 rv-typemenu__tick" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-iconchip${chipMenu === "filter" ? " is-open" : ""}${
                    totalFilterCount ? " is-on" : ""
                  }`}
                  onClick={() =>
                    setChipMenu((m) => (m === "filter" ? null : "filter"))
                  }
                  title="Filters"
                >
                  <i className="bi bi-funnel-fill" />
                  {totalFilterCount > 0 && (
                    <span className="rv-iconchip__count">{totalFilterCount}</span>
                  )}
                </button>

                {chipMenu === "filter" && (
                  <div className="rv-filtermenu">
                    <div className="rv-filtermenu__grid">
                      {ATTRIBUTE_FILTERS.map((f) => (
                        <React.Fragment key={f.key}>
                          <span className="rv-filtermenu__label">{f.label}</span>
                          <ValueFilterDropdown
                            icon={f.icon}
                            label={f.label}
                            plural={f.plural}
                            options={attrOptions[f.key] || []}
                            selected={attrFilters[f.key] || ["All"]}
                            onChange={(v) => setAttrFilter(f.key, v)}
                          />
                        </React.Fragment>
                      ))}

                      <span className="rv-filtermenu__label">Status</span>
                      <StatusFilterDropdown
                        selectedStatuses={statusFilter}
                        onChange={setStatusFilter}
                        className="w-100"
                      />

                      <span className="rv-filtermenu__label">Scope</span>
                      <select
                        className="rv-filtermenu__select"
                        value={reportScope}
                        onChange={(e) => setReportScope(e.target.value)}
                      >
                        <option value="all">All work ({scopeCounts.total})</option>
                        <option value="pending">
                          Pending only ({scopeCounts.pending})
                        </option>
                      </select>

                      <span className="rv-filtermenu__label">Advanced</span>
                      <button
                        type="button"
                        className={`rv-filtermenu__select rv-filtermenu__adv${
                          activeFilterCount ? " is-on" : ""
                        }`}
                        onClick={() => {
                          setChipMenu(null);
                          setShowFilterPanel(true);
                        }}
                      >
                        <i
                          className={`bi ${
                            activeFilterCount ? "bi-funnel-fill" : "bi-funnel"
                          }`}
                        />
                        {activeFilterCount
                          ? `${activeFilterCount} column filter${
                              activeFilterCount === 1 ? "" : "s"
                            }`
                          : "Add column filters…"}
                      </button>
                    </div>

                    <p className="rv-filtermenu__note">
                      <i className="bi bi-check-circle" />
                      Changes apply to the report as you make them.
                    </p>
                  </div>
                )}
              </div>
            </div>
          }
          title="Detailed Query Report"
          subtitle={
            activePrintGroup.key === "none" ? null : activePrintGroup.label
          }
          dateRange={dateRange}
          columns={printColumns}
          rows={filteredData}
          groupBy={
            activePrintGroup.key === "none"
              ? null
              : {
                  key: activePrintGroup.key,
                  /* "Client Wise" as a heading prefix would read "Client Wise
                     : SKYRP" — the type's own word is what belongs there. */
                  label: activePrintGroup.label.replace(/ Wise$/, ""),
                  value:
                    activePrintGroup.value ??
                    ((r) => r[activePrintGroup.key] || "(none)"),
                }
          }
        />
      </div>

      <DownloadProgressModal
        isOpen={isDownloading}
        progress={downloadProgress}
        fileName="Detailed Query Report.pdf"
        onClose={() => setIsDownloading(false)}
      />

      {/* ---> HISTORY MODAL POPUP <--- */}
      <Modal
        show={isHistoryModalOpen}
        onClose={closeHistoryModal}
        title="Task History Timeline"
        maxWidth="750px"
      >
        <div className="p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {selectedHistoryTask && (
            <div className="mb-4 pb-3 border-bottom d-flex justify-content-between align-items-start">
              <div>
                <h5 className="fw-bold text-primary mb-1">{selectedHistoryTask.TaskName}</h5>
                <div className="text-muted small">{selectedHistoryTask.NatureofQuery}</div>
              </div>
              <span className="badge bg-light text-secondary border px-3 py-2">
                Query #{selectedHistoryTask.QueryNo}
              </span>
            </div>
          )}

          {historyStatus === "loading" ? (
            <div className="text-center py-5"><Loader isLoading={true} /></div>
          ) : (() => {
            const rawHistoryList = Array.isArray(historyData) ? historyData : historyData?.data || [];

            // --- FIX: SORT DESCENDING (NEWEST FIRST) ---
            const historyList = [...rawHistoryList].sort((a, b) => {
              return new Date(b.ChangeTime) - new Date(a.ChangeTime);
            });

            return historyList.length > 0 ? (
              <div className="timeline-container ms-2 mt-4 pb-2">
                {historyList.map((h, i) => {
                  const statusColor = getTimelineColor(h.ChangeStatus);
                  const isLast = i === historyList.length - 1;

                  return (
                    <div key={i} className="timeline-item position-relative mb-4" style={{ paddingLeft: "2rem" }}>
                      {!isLast && (
                        <div
                          style={{
                            position: "absolute", left: "9px", top: "16px", bottom: "-24px",
                            width: "2px", backgroundColor: statusColor, zIndex: 1
                          }}
                        ></div>
                      )}
                      <div
                        className="timeline-dot position-absolute rounded-circle shadow-sm"
                        style={{
                          width: "14px", height: "14px", left: "3px", top: "4px",
                          border: "2px solid #fff", backgroundColor: statusColor, zIndex: 2
                        }}
                      ></div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>
                          Changed to: <span style={{ color: statusColor }}>{h.ChangeStatus}</span>
                        </span>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {formatDateTimeDisplay(h.ChangeTime)}
                        </span>
                      </div>
                      <div className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                        By: <span className="fw-medium text-dark">{h.UserName}</span>
                      </div>
                      {h.Description && (
                        <div className="bg-light p-2 mt-2 rounded text-muted" style={{ fontSize: "0.8rem", borderLeft: `3px solid ${statusColor}` }}>
                          {h.Description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted text-center small py-5">
                <i className="bi bi-clock-history fs-3 d-block mb-2 text-light"></i>
                No history records found for this task yet.
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* INLINE EXPORT MODAL */}
      {isExportModalOpen && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal d-block" tabIndex="-1" style={{ zIndex: 1050, backgroundColor: "rgba(0,0,0,0.4)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 border-0 shadow-lg" style={{ padding: "10px" }}>
                <div className="modal-header border-0 pb-2">
                  <h4 className="modal-title fw-bold text-dark">Export Report</h4>
                  <button type="button" className="btn-close bg-light rounded-circle p-2" onClick={() => !isExportingExcel && setIsExportModalOpen(false)} disabled={isExportingExcel} style={{ fontSize: "12px" }}></button>
                </div>
                <div className="modal-body pt-0 pb-3">
                  <p className="text-muted mb-4" style={{ fontSize: "0.95rem" }}>Choose the format you want to generate.</p>

                  <div className="border rounded-4 p-3 mb-3" style={{ borderColor: "#e9ecef" }}>
                    <div className="d-flex gap-3 align-items-start mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px", backgroundColor: "#e8f5e9" }}>
                        <i className="bi bi-file-earmark-excel-fill fs-4" style={{ color: "#2e7d32" }}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">Support Team Report</h6>
                        <p className="text-muted small mb-0">Generate MD's daily work progress format in Excel (.xlsx)</p>
                      </div>
                    </div>

                    <div className="bg-light p-3 rounded-3 mt-2 border">
                      <label className="small fw-bold text-dark mb-2">Select Date for Excel Export:</label>
                      <DateRangeFilter startDate={exportDateRange.startDate} endDate={exportDateRange.endDate} onChange={(s, e) => setExportDateRange({ startDate: s, endDate: e })} />
                      <div className="text-end mt-3">
                        <button className="btn btn-success fw-bold px-4 rounded-pill" onClick={handleExportExcelWithAPI} disabled={isExportingExcel}>
                          {isExportingExcel ? <><span className="spinner-border spinner-border-sm me-2"></span>Fetching Data...</> : "Download Excel"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    className="border rounded-4 p-3 d-flex gap-3 align-items-center cursor-pointer"
                    style={{ borderColor: "#e9ecef", cursor: "pointer", transition: "all 0.2s", opacity: isExportingExcel ? 0.5 : 1, pointerEvents: isExportingExcel ? "none" : "auto" }}
                    onClick={() => { if (!isExportingExcel) { handleExportPDF(); setIsExportModalOpen(false); } }}
                    onMouseEnter={(e) => !isExportingExcel && (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                    onMouseLeave={(e) => !isExportingExcel && (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px", backgroundColor: "#fce4e4" }}>
                      <i className="bi bi-file-earmark-pdf-fill fs-4" style={{ color: "#d32f2f" }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1 text-dark">Normal Report</h6>
                      <p className="text-muted small mb-0">Generate standard layout in PDF format (.pdf) from table</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;